import {
  BIRTH_NOTIFICATION_STATUSES,
  BIRTH_ORDER_MAX,
  BIRTH_OUTCOMES,
  BIRTH_WEIGHT_GRAMS,
  DEATH_CAUSE_ROLES,
  DEATH_MANNERS,
  DEATH_PLACES,
  DEATH_RECORD_STATUSES,
  MORTUARY_CASE_STATUSES,
} from '@wodi/domain';
import { z } from 'zod';

import { printedPatientSchema } from './clinical-documents.ts';
import { actorSchema, codedRefSchema, codedValueSchema, reasonSchema } from './clinical-common.ts';
import { terminologyCodingSchema } from './terminology.ts';

// Death and birth notification (docs/plans/phase2-mortuary-and-notifications.md §3). All of
// it is sensitive: causes, notes, names and weights travel in bodies only, never in a URL,
// and are never logged. Records are addressed by internal UUIDs.

/** The request states corrections and releases share: one asks, another decides. */
export const VITAL_REQUEST_STATUSES = ['requested', 'approved', 'rejected', 'withdrawn'] as const;
export type VitalRequestStatus = (typeof VITAL_REQUEST_STATUSES)[number];

/** Approve or reject (a note is required to reject) or withdraw. */
export const vitalDecisionBodySchema = z.object({
  note: z.string().trim().min(1).max(500).optional(),
});

/** Who a record is about, as lists show them. Names are PHI: bodies only. */
export const vitalPersonSchema = z.object({
  id: z.uuid(),
  displayName: z.string(),
  patientNumber: z.string(),
});
export type VitalPerson = z.infer<typeof vitalPersonSchema>;

/** The places a death is recorded as from a visit here (not a death elsewhere). */
export const RECORDABLE_DEATH_PLACES = ['ward', 'casualty', 'brought_in_dead'] as const;

// --- Deaths ------------------------------------------------------------------------------------

const clinicianTextSchema = z.string().trim().min(1).max(500);

export const deathCauseInputSchema = z
  .object({
    role: z.enum(DEATH_CAUSE_ROLES),
    /** From the configured ICD-11 terminology. Words alone only while none is set up. */
    coding: terminologyCodingSchema.nullable(),
    clinicianText: clinicianTextSchema.nullable().default(null),
    /** Approximate interval between onset and death, e.g. "2 days". */
    onsetInterval: z.string().trim().min(1).max(100).nullable().default(null),
  })
  .refine((c) => c.coding !== null || (c.clinicianText ?? '').trim() !== '', {
    message: 'Choose a code, or describe the condition in words',
  });
export type DeathCauseInput = z.input<typeof deathCauseInputSchema>;

export const recordDeathBodySchema = z.object({
  /** Generated once per form; a retried request returns the same record. */
  clientRequestId: z.uuid(),
  /** Clinical event time: during the visit, not in the future. */
  diedAt: z.iso.datetime(),
  place: z.enum(RECORDABLE_DEATH_PLACES),
  /** The certifying clinician (a user who may record deaths here). */
  certifiedBy: z.uuid(),
  manner: z.enum(DEATH_MANNERS),
  causes: z.array(deathCauseInputSchema).max(12),
  notes: z.string().trim().min(1).max(2000).nullable().default(null),
});
export type RecordDeathBody = z.infer<typeof recordDeathBodySchema>;

export const deathCauseSchema = z.object({
  id: z.uuid(),
  role: z.enum(DEATH_CAUSE_ROLES),
  position: z.number().int().min(1),
  code: z.string().nullable(),
  display: z.string().nullable(),
  codeSystemUri: z.string().nullable(),
  codeSystemVersion: z.string().nullable(),
  clinicianText: z.string().nullable(),
  onsetInterval: z.string().nullable(),
});

export const deathCorrectionSchema = z.object({
  id: z.uuid(),
  reason: z.string(),
  status: z.enum(VITAL_REQUEST_STATUSES),
  requestedBy: actorSchema,
  requestedAt: z.iso.datetime(),
  decidedBy: actorSchema.nullable(),
  decidedAt: z.iso.datetime().nullable(),
  decisionNote: z.string().nullable(),
});
export type DeathCorrectionView = z.infer<typeof deathCorrectionSchema>;

export const deathRecordSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  visitId: z.uuid(),
  admissionId: z.uuid().nullable(),
  facility: z.object({ id: z.uuid(), name: z.string() }),
  place: z.enum(DEATH_PLACES),
  diedAt: z.iso.datetime(),
  certifiedBy: actorSchema.nullable(),
  manner: z.enum(DEATH_MANNERS).nullable(),
  notes: z.string().nullable(),
  causes: z.array(deathCauseSchema),
  status: z.enum(DEATH_RECORD_STATUSES),
  recordedAt: z.iso.datetime(),
  recordedBy: actorSchema,
  enteredInError: z
    .object({
      reason: z.string(),
      requestedBy: actorSchema,
      approvedBy: actorSchema,
      at: z.iso.datetime(),
    })
    .nullable(),
  corrections: z.array(deathCorrectionSchema),
  mortuaryCase: z
    .object({ id: z.uuid(), caseNumber: z.string(), status: z.enum(MORTUARY_CASE_STATUSES) })
    .nullable(),
  printCount: z.number().int().min(0),
  /** The record entered in error that this one replaces, on the same stay (plan D14). */
  supersedesId: z.uuid().nullable(),
});
export type DeathRecordView = z.infer<typeof deathRecordSchema>;

export const patientDeathsSchema = z.object({ items: z.array(deathRecordSchema) });

export const deathListItemSchema = z.object({
  id: z.uuid(),
  patient: vitalPersonSchema,
  place: z.enum(DEATH_PLACES),
  diedAt: z.iso.datetime(),
  status: z.enum(DEATH_RECORD_STATUSES),
  mortuaryCaseId: z.uuid().nullable(),
});
export type DeathListItem = z.infer<typeof deathListItemSchema>;
export const deathListSchema = z.object({ items: z.array(deathListItemSchema) });

/** The people who may certify a death here (TODO(clinical-verify) D5). */
export const deathFormOptionsSchema = z.object({
  certifiers: z.array(z.object({ id: z.uuid(), displayName: z.string() })),
});

/**
 * Whether a death can be recorded on a visit: its open stay or casualty visit, or the stay a
 * death entered in error ended (`replacesDeathId`, plan D14). `birthCheck` asks for the
 * passive reminder that a birth on this visit is not recorded (plan D16).
 */
export const deathVisitFormSchema = z.object({
  recordable: z.boolean(),
  replacesDeathId: z.uuid().nullable(),
  birthCheck: z.boolean(),
});
export type DeathVisitForm = z.infer<typeof deathVisitFormSchema>;

export const requestDeathCorrectionBodySchema = z.object({
  clientRequestId: z.uuid(),
  reason: reasonSchema,
});

export const deathPrintSchema = z.object({
  death: deathRecordSchema,
  facility: z.object({ name: z.string() }),
  patient: printedPatientSchema,
  /** The copy this print will be: 1 for the first, "Copy 2" after. */
  copy: z.number().int().min(1),
});
export type DeathPrintView = z.infer<typeof deathPrintSchema>;

// --- Births ------------------------------------------------------------------------------------

export const recordBirthBodySchema = z.object({
  clientRequestId: z.uuid(),
  /** Clinical event time: during the mother's visit, not in the future. */
  bornAt: z.iso.datetime(),
  /** From the tenant's sex value set. */
  sex: codedRefSchema,
  /** Whole grams (TODO(clinical-verify) N3: a data-entry bound, not a clinical threshold). */
  birthWeightGrams: z.number().int().min(BIRTH_WEIGHT_GRAMS.min).max(BIRTH_WEIGHT_GRAMS.max),
  attendantUserId: z.uuid(),
  /** For twins and more; null for a single birth. */
  birthOrder: z.number().int().min(1).max(BIRTH_ORDER_MAX).nullable().default(null),
});
export type RecordBirthBody = z.infer<typeof recordBirthBodySchema>;

export const birthNotificationSchema = z.object({
  id: z.uuid(),
  facility: z.object({ id: z.uuid(), name: z.string() }),
  mother: vitalPersonSchema,
  motherVisitId: z.uuid(),
  baby: vitalPersonSchema,
  outcome: z.enum(BIRTH_OUTCOMES),
  bornAt: z.iso.datetime(),
  sex: codedValueSchema,
  birthWeightGrams: z.number().int(),
  attendant: actorSchema,
  birthOrder: z.number().int().nullable(),
  status: z.enum(BIRTH_NOTIFICATION_STATUSES),
  recordedAt: z.iso.datetime(),
  recordedBy: actorSchema,
  enteredInError: z
    .object({ reason: z.string(), by: actorSchema, at: z.iso.datetime() })
    .nullable(),
  printCount: z.number().int().min(0),
});
export type BirthNotificationView = z.infer<typeof birthNotificationSchema>;

export const patientBirthsSchema = z.object({
  /** Births this patient gave here. */
  asMother: z.array(birthNotificationSchema),
  /** This patient's own birth here, when they were born here. */
  asBaby: birthNotificationSchema.nullable(),
});
export type PatientBirthsView = z.infer<typeof patientBirthsSchema>;

export const birthListItemSchema = z.object({
  id: z.uuid(),
  mother: vitalPersonSchema,
  baby: vitalPersonSchema,
  bornAt: z.iso.datetime(),
  sexDisplay: z.string(),
  birthWeightGrams: z.number().int(),
  status: z.enum(BIRTH_NOTIFICATION_STATUSES),
});
export type BirthListItem = z.infer<typeof birthListItemSchema>;
export const birthListSchema = z.object({ items: z.array(birthListItemSchema) });

export const birthFormOptionsSchema = z.object({
  attendants: z.array(z.object({ id: z.uuid(), displayName: z.string() })),
  /** The tenant's sex value set, in its order. */
  sexes: z.array(codedValueSchema),
});
export type BirthFormOptions = z.infer<typeof birthFormOptionsSchema>;

export const birthPrintSchema = z.object({
  birth: birthNotificationSchema,
  facility: z.object({ name: z.string() }),
  mother: printedPatientSchema,
  baby: printedPatientSchema,
  copy: z.number().int().min(1),
});
export type BirthPrintView = z.infer<typeof birthPrintSchema>;

export const printCopySchema = z.object({ copy: z.number().int().min(1) });
